"use client";

import { unified } from "unified";
import remarkParse from "remark-parse";
import remarkGfm from "remark-gfm";
import remarkRehype from "remark-rehype";
import rehypeStringify from "rehype-stringify";
import TurndownService from "turndown";
import rehypeRaw from "rehype-raw";

// Slide-level directives parsing: ::bg=#xxxxxx, ::text=#xxxxxx, ::layout=two-column
export type SlideMeta = {
  bg?: string;
  text?: string;
  bgImage?: string;
  layout?: "two-column" | "title" | "content";
  align?: "left" | "center" | "right";
};

// Extract directives from the head of the Markdown (lines beginning with ::key=value)
export function parseSlideMeta(md: string): SlideMeta {
  const meta: SlideMeta = {};
  const lines = md.replace(/\r\n/g, "\n").split("\n");
  let consumed = 0;
  for (const ln of lines) {
    const t = ln.trim();
    if (!t.startsWith("::")) break;
    const m = t.match(/^::\s*([a-zA-Z]+)\s*=\s*(.+)$/);
    if (m) {
      const key = m[1].toLowerCase();
      const val = m[2].trim();
      if (key === "bg") meta.bg = val;
      else if (key === "text") meta.text = val;
      else if (key === "bgimage" || key === "bg_image" || key === "bg-img") meta.bgImage = val;
      else if (key === "layout" && (val === "two-column" || val === "title" || val === "content")) meta.layout = val as SlideMeta["layout"];
      else if (key === "align") {
        const a = val.toLowerCase();
        if (a === "left" || a === "center" || a === "right") meta.align = a as SlideMeta["align"];
      }
    }
    consumed++;
  }
  return meta;
}

// Remove directive header before content rendering
export function stripSlideMeta(md: string): string {
  const lines = md.replace(/\r\n/g, "\n").split("\n");
  let i = 0;
  while (i < lines.length && lines[i].trim().startsWith("::")) i++;
  return lines.slice(i).join("\n");
}

// Transform standalone YouTube URLs into embed HTML blocks (handled as raw HTML by rehype-raw)
function transformYouTube(md: string): string {
  const lines = md.replace(/\r\n/g, "\n").split("\n");
  const ytRegex = /^(https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=[\w-]+|youtu\.be\/[\w-]+)(?:[^\s]*)?)$/i;
  return lines
    .map((ln) => {
      const m = ln.trim().match(ytRegex);
      if (!m) return ln;
      const url = m[1];
      // Extract video id
      let vid = "";
      const idFromYoutuBe = url.match(/youtu\.be\/([\w-]+)/i)?.[1];
      const idFromWatch = url.match(/v=([\w-]+)/i)?.[1];
      vid = idFromYoutuBe || idFromWatch || "";
      if (!vid) return ln;
      // Replace with responsive iframe HTML
      return `<div style='position:relative;padding-bottom:56.25%;height:0;overflow:hidden;border-radius:0.5rem'>
  <iframe src="https://www.youtube.com/embed/${vid}" title="YouTube video" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen style='position:absolute;top:0;left:0;width:100%;height:100%'></iframe>
</div>`;
    })
    .join("\n");
}

/**
 * Convert Markdown to HTML suitable for TipTap/Presenter.
 * Supports:
 * - slide directives (::bg=, ::text=, ::layout=two-column)
 * - columns: when layout=two-column, split content on a line containing only "||"
 * - YouTube auto-embed from standalone URLs
 */
export async function markdownToHtml(md: string): Promise<string> {
  const meta = parseSlideMeta(md);
  const bodyMd = transformYouTube(stripSlideMeta(md));

  // Two-column layout: split by a delimiter line "||" (allow surrounding whitespace)
  if (meta.layout === "two-column") {
    // Allow "||" with optional surrounding whitespace on its own line
    const SPLIT_RE = /\n\s*\|\|\s*\n/;
    const parts = bodyMd.split(SPLIT_RE);
    if (parts.length === 2) {
      const [leftMd, rightMd] = parts;
      const leftFile = await unified()
        .use(remarkParse)
        .use(remarkGfm)
        .use(remarkRehype, { allowDangerousHtml: true })
        .use(rehypeRaw)
        .use(rehypeStringify, { allowDangerousHtml: true })
        .process(leftMd);
      const rightFile = await unified()
        .use(remarkParse)
        .use(remarkGfm)
        .use(remarkRehype, { allowDangerousHtml: true })
        .use(rehypeRaw)
        .use(rehypeStringify, { allowDangerousHtml: true })
        .process(rightMd);

      // Inline styles to avoid reliance on Tailwind class detection in HTML strings
      const gridStyle = "display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:1.5rem;align-items:start";
      return `<div style="${gridStyle}"><div>${String(leftFile)}</div><div>${String(rightFile)}</div></div>`;
    }
  }

  // Single column default: render entire markdown
  const file = await unified()
    .use(remarkParse)
    .use(remarkGfm)
    .use(remarkRehype, { allowDangerousHtml: true })
    .use(rehypeRaw)
    .use(rehypeStringify, { allowDangerousHtml: true })
    .process(bodyMd);
  return String(file);
}

/**
 * Convert TipTap HTML back to Markdown (best-effort).
 * Preserve inline styles (color, background-color, font-family, font-size) and image width by emitting HTML.
 */
export function htmlToMarkdown(html: string): string {
  const turndown = new TurndownService({
    headingStyle: "atx",
    codeBlockStyle: "fenced",
    bulletListMarker: "-",
  });

  // Keep raw <img> and <iframe> as HTML blocks in Markdown
  turndown.keep(["img", "iframe"]);

  // Preserve images with width/style by emitting HTML <img> tags including width attribute
  turndown.addRule("keepImgFull", {
    filter: "img",
    replacement(content, node) {
      const el = node as HTMLElement;
      const src = el.getAttribute("src") || "";
      const alt = el.getAttribute("alt") || "";
      let style = (el.getAttribute("style") || "").trim();
      let widthAttr = el.getAttribute("width");

      // If width attribute missing, try extracting from style
      if (!widthAttr) {
        const m = style.match(/width\s*:\s*(\d+)\s*px/i);
        if (m) widthAttr = m[1];
      }

      // Ensure style contains width if we have a width number
      if (widthAttr && !/width\s*:/i.test(style)) {
        style = `${style ? style + "; " : ""}width: ${parseInt(widthAttr, 10)}px`;
      }

      const widthPart = widthAttr ? ` width="${parseInt(widthAttr, 10)}"` : "";
      const stylePart = style ? ` style='${style}'` : "";
      return `<img src="${src}" alt="${alt}"${widthPart}${stylePart} />`;
    },
  });

  // Preserve span styles (color/background/font-family/font-size)
  turndown.addRule("preserveSpanStyles", {
    filter: (node) => {
      return node.nodeName === "SPAN" && ((node as HTMLElement).getAttribute("style") || "").length > 0;
    },
    replacement(content, node) {
      const style = ((node as HTMLElement).getAttribute("style") || "").trim();
      if (!style) return content;
      const allowed = ["color", "background-color", "font-family", "font-size"];
      const filtered = style
        .split(";")
        .map((d) => d.trim())
        .filter(Boolean)
        .filter((decl) => {
          const [prop] = decl.split(":");
          return allowed.includes((prop || "").trim().toLowerCase());
        })
        .join("; ");
      return filtered ? `<span style='${filtered}'>${content}</span>` : content;
    },
  });

  // Preserve mark highlight styles
  turndown.addRule("preserveMarkHighlight", {
    filter: "mark",
    replacement(content, node) {
      const style = ((node as HTMLElement).getAttribute("style") || "").trim();
      let filtered = "";
      if (style) {
        filtered = style
          .split(";")
          .map((d) => d.trim())
          .filter(Boolean)
          .filter((decl) => {
            const [prop] = decl.split(":");
            return (prop || "").trim().toLowerCase() === "background-color";
          })
          .join("; ");
      }
      return filtered ? `<mark style='${filtered}'>${content}</mark>` : `<mark>${content}</mark>`;
    },
  });

  return turndown.turndown(html).trim();
}

/**
 * Split a Markdown deck into slides. We use '---' on its own line to separate slides.
 */
export function splitDeck(mdDeck: string): string[] {
  const parts = mdDeck
    .replace(/\r\n/g, "\n")
    .split(/\n-{3,}\n/g)
    .map((p) => p.trim())
    .filter((p) => p.length > 0);
  return parts.length ? parts : [mdDeck.trim()];
}

/**
 * Join slides into a single deck MD string.
 */
export function joinDeck(slides: string[]): string {
  return slides.map((s) => s.trim()).join("\n\n---\n\n");
}

/**
 * Extract a human-friendly title from a slide Markdown (first H1/H2 or first non-empty line).
 */
export function extractSlideTitle(slideMd: string): string {
  // Remove any directive header first so we don't pick ::bg= as the title
  const body = stripSlideMeta(slideMd);
  const lines = body.replace(/\r\n/g, "\n").split("\n");

  // Unicode ranges for emoji/dingbats/flags
  const emojiRe = /[\u{2700}-\u{27BF}\u{1F300}-\u{1FAFF}\u{1F1E6}-\u{1F1FF}\u{FE0F}]+/u;

  const clean = (s: string) => {
    let t = s.trim();
    // Ignore leading heading markers
    t = t.replace(/^#{1,6}\s+/, "").trim();
    // Normalize "Slide N —|-|: Title" patterns
    const mSlide = t.match(/^(?:\*\*)?\s*Slide\s+\d+\s*(?:—|-|:)\s*(.+?)(?:\*\*)?$/i);
    if (mSlide) t = mSlide[1].trim();
    // Strip leading emoji/dingbats/icons
    t = t.replace(new RegExp(`^${emojiRe.source}\\s*`, 'u'), "").trim();
    // Strip inline HTML tags (avoid showing raw tags)
    t = t.replace(/<[^>]+>/g, "").trim();
    // Collapse multiple spaces
    t = t.replace(/\s{2,}/g, " ");
    return t;
  };

  // Helpers for image detection and alt extraction
  const mdImgAlt = (s: string) => {
    const m = s.match(/^!\[([^\]]*)\]\([^)]+\)/);
    return m ? (m[1] || "").trim() : "";
  };
  const htmlImgAlt = (s: string) => {
    const m = s.match(/<img[^>]*alt=['"]([^'"]*)['"][^>]*>/i);
    return m ? (m[1] || "").trim() : "";
  };
  const isOnlyMediaLine = (s: string) =>
    /^!\[.*\]\([^)]+\)\s*$/.test(s) ||
    /<\s*img\b/i.test(s) ||
    /<\s*iframe\b/i.test(s) ||
    /<\s*video\b/i.test(s) ||
    /^\s*```/.test(s);

  for (const ln of lines) {
    const t0 = ln.trim();
    if (!t0) continue;
    if (t0.startsWith("::")) continue;          // skip directives
    if (t0 === "||") continue;                  // skip column delimiter
    if (/^<\/?div(\s|>|$)/i.test(t0)) continue; // skip wrapper div opens/closes
    if (/^\s*-{3,}\s*$/.test(t0)) continue;     // skip separators like '---'

    // Skip media/code lines; use alt text for images if present
    if (isOnlyMediaLine(t0)) {
      const alt = mdImgAlt(t0) || htmlImgAlt(t0);
      if (alt) return alt.length > 80 ? alt.slice(0, 77) + "..." : alt;
      continue;
    }

    const t = clean(t0);
    if (t) return t.length > 80 ? t.slice(0, 77) + "..." : t;
  }

  // Fallbacks: if slide contains any image, use its alt or a friendly label
  const anyMdImg = body.match(/!\[([^\]]*)\]\([^)]+\)/);
  const anyHtmlImg = body.match(/<img[^>]*alt=['"]([^'"]*)['"][^>]*>/i);
  if (anyMdImg) {
    const alt = (anyMdImg[1] || "").trim();
    return alt ? (alt.length > 80 ? alt.slice(0, 77) + "..." : alt) : "Image";
  }
  if (anyHtmlImg) {
    const alt = (anyHtmlImg[1] || "").trim();
    return alt ? (alt.length > 80 ? alt.slice(0, 77) + "..." : alt) : "Image";
  }

  return "Untitled";
}

// Split full slide Markdown into two columns (based on "||" on its own line), ignoring directives.
export function splitTwoColumnBody(md: string): { left: string; right: string } | null {
  const body = stripSlideMeta(md);
  const SPLIT_RE = /\n\s*\|\|\s*\n/;
  const parts = body.split(SPLIT_RE);
  if (parts.length === 2) {
    return { left: parts[0], right: parts[1] };
  }
  return null;
}

// Render a Markdown fragment (no directives, no column wrapper) to HTML.
export async function markdownBodyToHtml(mdBody: string): Promise<string> {
  const processed = await unified()
    .use(remarkParse)
    .use(remarkGfm)
    .use(remarkRehype, { allowDangerousHtml: true })
    .use(rehypeRaw)
    .use(rehypeStringify, { allowDangerousHtml: true })
    .process(transformYouTube(mdBody));
  return String(processed);
}